import { useState } from "react";
import { Lightbulb } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";
import { Button } from "@/components/flowly/infra/Button/Button";
import { FLOW_STEPS } from "./constants";
import type { FlowId } from "./types";

interface HowItWorksProps {
  flowId: FlowId;
}

export const HowItWorks = ({ flowId }: HowItWorksProps) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const steps = FLOW_STEPS[flowId];
  const flowT = t.howItWorks[flowId as keyof typeof t.howItWorks] as Record<
    string,
    string
  >;

  return (
    <>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => setIsOpen(true)}
        className="text-xs underline-offset-2 hover:underline !p-0 text-orange-500"
      >
        <Lightbulb className="w-3.5 h-3.5" />
        {t.howItWorks.trigger}
      </Button>

      <ModalCard
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
      >
        <div className="pt-2">
          {/* Headline */}
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-8 text-center">
            {flowT.headline}
          </h3>

          {/* Steps Grid */}
          <div
            className={cn(
              "grid grid-cols-1 gap-6",
              steps.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3",
            )}
          >
            {steps.map((step, index) => {
              const Icon = step.iconComponent;
              return (
                <div
                  key={step.titleKey}
                  className="flex flex-col items-center text-center gap-3"
                  style={{
                    animation: `fade-in 0.4s ease-out ${index * 0.1}s both`,
                  }}
                >
                  {/* Icon Circle */}
                  <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>

                  {/* Step Badge */}
                  <span className="bg-primary/15 text-primary text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {index + 1}
                  </span>

                  {/* Title */}
                  <h4 className="text-base font-bold text-foreground">
                    {flowT[step.titleKey]}
                  </h4>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground">
                    {flowT[step.descKey]}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Got It Button */}
          <div className="mt-8 flex justify-center">
            <Button
              variant="primary"
              size="md"
              rounded="xl"
              onClick={() => setIsOpen(false)}
            >
              {t.howItWorks.gotIt}
            </Button>
          </div>
        </div>
      </ModalCard>
    </>
  );
};
